import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  ActiveErrorData,
  DevicesSelectors,
  DeviceTypes,
  DisplayedErrorState,
  InstallationDetailsSelectors,
  InstallationDetailsState,
  InstallationsState,
  RichDevice,
} from '@store';
import { stickyPanel } from 'e2e/constants';

import { AppState } from '../app.state';

import { ACTIVE_ERROR_TYPE } from './active-errors.model';
import { ActiveErrorsState } from './active-errors.state';
import { ActiveErrorsHelper } from './active-errors.helper';
import { ActiveErrorStatus } from '@vi-iot/client/index';

export namespace ActiveErrorsSelectors {
  export const activeErrors = (state: AppState): ActiveErrorsState => state.activeErrors;
  export const installations = (state: AppState): InstallationsState => state.installations;
  export const installationDetails = (state: AppState): InstallationDetailsState =>
    state.installationDetails;

  export const isLoading = createSelector(
    activeErrors,
    (state: ActiveErrorsState) => state.loading
  );
  export const loaded = createSelector(activeErrors, (state: ActiveErrorsState) => state.loaded);

  export function getState(): MemoizedSelector<
    AppState,
    ActiveErrorsState,
    DefaultProjectorFn<ActiveErrorsState>
  > {
    return createSelector(activeErrors, (state: ActiveErrorsState) => state);
  }

  export function getActiveGatewayErrorsForTrending(): MemoizedSelector<
    AppState,
    ActiveErrorData[],
    DefaultProjectorFn<ActiveErrorData[]>
  > {
    return createSelector(
      activeErrors,
      InstallationDetailsSelectors.getGatewaySerial(),
      (state: ActiveErrorsState, gatewaySerial: string) => {
        let activeErrorsData =
          (state.activeErrors &&
            state.activeErrors.filter(
              (error) => !!error.errorCode && error.gatewaySerial === gatewaySerial
            )) ||
          [];

        return activeErrorsData;
      }
    );
  }

  export function getActiveGatewayErrors(
    gatewaySerial: string,
    boilerSerial: string | undefined,
    includeDeviceTypes = false,
    withDetailsOnly = false
  ): MemoizedSelector<AppState, ActiveErrorData[], DefaultProjectorFn<ActiveErrorData[]>> {
    return createSelector(
      activeErrors,
      DevicesSelectors.getDevicesOfSelectedGateway(),
      (state: ActiveErrorsState, allV2CascadeDevices: RichDevice[]) => {
        let activeErrorsData =
          (state.activeErrors &&
            state.activeErrors.filter(
              (error) => !!error.errorCode && error.gatewaySerial === gatewaySerial
            )) ||
          [];

        if (withDetailsOnly) {
          activeErrorsData = activeErrorsData.filter((error) => error.details);
        }

        if (boilerSerial) {
          activeErrorsData = activeErrorsData.filter((error) => {
            if (error.deviceId === '0') {
              return error.deviceSerial === boilerSerial;
            } else {
              return error.deviceId === boilerSerial;
            }
          });
        }

        if (includeDeviceTypes) {
          const activeErrorsWithDeviceType: ActiveErrorData[] = activeErrorsData.map(
            (activeError: ActiveErrorData) => {
              const deviceType = allV2CascadeDevices?.find(
                (device) =>
                  device.id === activeError.deviceId &&
                  device.gatewaySerial === activeError.gatewaySerial
              )?.deviceType;

              activeError.deviceType = deviceType ?? DeviceTypes.UnknownType;

              return activeError;
            }
          );

          return activeErrorsWithDeviceType;
        }

        return activeErrorsData;
      }
    );
  }

  export function getActiveErrorsForSRCDevice(
    deviceId: string
  ): MemoizedSelector<AppState, ActiveErrorData[], DefaultProjectorFn<ActiveErrorData[]>> {
    return createSelector(
      activeErrors,
      (state: ActiveErrorsState) =>
        (state.activeErrors &&
          state.activeErrors.filter((error) => !!error.errorCode && error.deviceId === deviceId)) ||
        []
    );
  }

  export function isLowBatteryStatusOfSRCDevice(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      activeErrors,
      (state: ActiveErrorsState) =>
        state.activeErrors &&
        state.activeErrors.some(
          (error) => error.type === ACTIVE_ERROR_TYPE.BATTERY_LOW && error.deviceId === deviceId
        )
    );
  }

  export function getDisplayedActiveErrorsAndNotificationsModel(
    gatewaySerial: string,
    includeDeviceTypes: boolean,
    withDetailsOnly: boolean
  ): MemoizedSelector<AppState, DisplayedErrorState, DefaultProjectorFn<DisplayedErrorState>> {
    return createSelector(
      activeErrors,
      getActiveGatewayErrors(gatewaySerial, '', includeDeviceTypes, withDetailsOnly),
      getMaintenanceNotificationsDistinctByType(gatewaySerial, includeDeviceTypes),
      (
        state: ActiveErrorsState,
        errorList: ActiveErrorData[],
        maintenanceNotificationList: ActiveErrorData[]
      ): DisplayedErrorState => {
        const ignoredSmartNotes = ActiveErrorsHelper.getLocalStorageIgnoredSmartNotes();

        return {
          countOfAllErrors: errorList.length,
          displayedErrors: state.showAllSideNavActiveErrors
            ? errorList
            : errorList.slice(0, stickyPanel.LIMITED_LIST_MAX_LENGTH),
          maintenanceNotifications: maintenanceNotificationList,
          powerLimitationStatuses: state.activeErrors.filter(
            (status) =>
              status.status === ActiveErrorStatus.New &&
              !ignoredSmartNotes.includes(status.id.toString()) &&
              (status.type === ACTIVE_ERROR_TYPE.POWER_LIMITATION_CONSUMPTION ||
                status.type === ACTIVE_ERROR_TYPE.POWER_LIMITATION_POWER_PRODUCTION ||
                status.type === ACTIVE_ERROR_TYPE.SRC_DEVICE_OFFLINE)
          ),
        };
      }
    );
  }

  export function getLockedSystemError(
    gatewaySerial: string
  ): MemoizedSelector<AppState, ActiveErrorData, DefaultProjectorFn<ActiveErrorData>> {
    return createSelector(activeErrors, (state: ActiveErrorsState) =>
      state.activeErrors.find(
        (error) =>
          error.gatewaySerial === gatewaySerial && error.type === ACTIVE_ERROR_TYPE.SYSTEM_LOCKED
      )
    );
  }

  export function getFloorHeatingOverHeatingError(
    gatewaySerial: string
  ): MemoizedSelector<AppState, ActiveErrorData, DefaultProjectorFn<ActiveErrorData>> {
    return createSelector(activeErrors, (state: ActiveErrorsState) =>
      state.activeErrors.find(
        (error) =>
          error.gatewaySerial === gatewaySerial &&
          error.type === ACTIVE_ERROR_TYPE.FLOOR_HEATING_OVERHEATING
      )
    );
  }

  export function isLockedSystemError(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(activeErrors, (state: ActiveErrorsState) =>
      state.activeErrors.some(
        (error) =>
          error.gatewaySerial === gatewaySerial && error.type === ACTIVE_ERROR_TYPE.SYSTEM_LOCKED
      )
    );
  }

  export function isDeviceOrLockedSystemError(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(activeErrors, (state: ActiveErrorsState): boolean =>
      state.activeErrors.some(
        (error) =>
          error.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR ||
          error.type === ACTIVE_ERROR_TYPE.SYSTEM_LOCKED
      )
    );
  }

  export const getSelectedActiveError = createSelector(
    activeErrors,
    (state: ActiveErrorsState): ActiveErrorData =>
      state.activeErrors.find((error) => error.isSelected)
  );

  export function isAnyErrorSelectedForDeviceBySerial(
    deviceSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(activeErrors, (state: ActiveErrorsState): boolean =>
      state.activeErrors
        .filter((error) => error.deviceSerial === deviceSerial)
        .some((error) => error.isSelected)
    );
  }

  export const isAnyActiveErrorLoading = createSelector(
    activeErrors,
    (state: ActiveErrorsState): boolean => {
      const loadingActiveError = state.activeErrors.find((error) => error.loading);

      return !!loadingActiveError && state.loading;
    }
  );

  export const hasAnyActiveErrorDetails = createSelector(
    activeErrors,
    (state: ActiveErrorsState): boolean => {
      const details = state.activeErrors.find((error) => error.details);

      return !!details;
    }
  );

  export const selectDeviceIdForLowBatteryNotification = createSelector(
    activeErrors,
    (state: ActiveErrorsState): string[] => {
      return state.activeErrors
        .filter((error) => error.type === ACTIVE_ERROR_TYPE.BATTERY_LOW)
        .map((error) => error.deviceId);
    }
  );

  export function getMaintenanceNotificationsDistinctByType(
    gatewaySerial: string,
    includeDeviceTypes = false
  ): MemoizedSelector<AppState, ActiveErrorData[], DefaultProjectorFn<ActiveErrorData[]>> {
    return createSelector(
      activeErrors,
      DevicesSelectors.getDevicesOfSelectedGateway(),
      (state: ActiveErrorsState, allV2CascadeDevices: RichDevice[]) => {
        let activeErrors = state?.activeErrors?.length > 0 ? state.activeErrors : [];

        const typesToMatch: string[] = [
          ACTIVE_ERROR_TYPE.FILTER_EXCHANGE,
          ACTIVE_ERROR_TYPE.SALT_CARTRIDGE_EXCHANGE,
          ACTIVE_ERROR_TYPE.MAINTENANCE_TIMEBASED,
          ACTIVE_ERROR_TYPE.FILTER_EXCHANGE_14_DAYS,
          ACTIVE_ERROR_TYPE.MAINTENANCE_BURNERBASED,
          ACTIVE_ERROR_TYPE.DEVICE_BATTERY_LEVEL_LOW,
          ACTIVE_ERROR_TYPE.FILTER_CHANGE_MODE_ACTIVATED,
          ACTIVE_ERROR_TYPE.COMFORT_ENSURING_MODE_CHANGED,
        ];

        if (activeErrors.find((entry) => entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED)) {
          typesToMatch.push(ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED);
        } else if (
          activeErrors.find((entry) => entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D)
        ) {
          typesToMatch.push(ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D);
        } else if (
          activeErrors.find((entry) => entry.type === ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D)
        ) {
          typesToMatch.push(ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D);
        } else if (
          activeErrors.find(
            (entry) => entry.type === ACTIVE_ERROR_TYPE.FILTER_CHANGE_MODE_ACTIVATED
          )
        ) {
          typesToMatch.push(ACTIVE_ERROR_TYPE.FILTER_CHANGE_MODE_ACTIVATED);
        }

        let notificationData = activeErrors.filter(
          (error: ActiveErrorData, index: number, errorsArray: ActiveErrorData[]) => {
            const serialMatch = error.gatewaySerial === gatewaySerial;
            const typeMatch = typesToMatch.includes(error.type);
            const indexMatch =
              index ===
              errorsArray.findIndex(
                (notification: ActiveErrorData) => notification.type === error.type
              );

            return serialMatch && typeMatch && indexMatch;
          }
        );

        if (includeDeviceTypes) {
          const notificationWithDeviceType: ActiveErrorData[] = notificationData.map(
            (activeError: ActiveErrorData) => {
              const deviceType = allV2CascadeDevices?.find(
                (device) =>
                  device.id === activeError.deviceId &&
                  device.gatewaySerial === activeError.gatewaySerial
              )?.deviceType;

              activeError.deviceType = deviceType ?? DeviceTypes.UnknownType;

              return activeError;
            }
          );

          return notificationWithDeviceType;
        }

        return notificationData;
      }
    );
  }
}
